import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAreasNav } from '../../contexts/AreasContext';
import {
  camerasAPI,
  isInsufficientFundsError,
  modelsAPI,
  notifyNotificationsChanged,
} from '../../utils/api';
import { getModelDisplayName } from '../../utils/modelLabels';
import {
  cameraHasPresenceCheckModel,
  DEFAULT_PRESENCE_SETTINGS,
  isPresenceCheckModel,
} from '../../utils/presence';
import '../../pages/Area/Area.css';
import './AddCameraModal.css';

interface AreaOption {
  id: number;
  name: string;
}

interface AddCameraModalProps {
  areas: AreaOption[];
  onClose: () => void;
  onSuccess?: () => void;
  defaultAreaId?: number | null;
}

const modalIconsBase = `${process.env.PUBLIC_URL}/icons/modals`;

function getModelModalIcon(model: string): string {
  const modelKey = (model || '').trim().toLowerCase();

  if (modelKey.includes('license_plate') || modelKey.includes('plate')) {
    return 'lock.svg';
  }
  if (modelKey === 'fire_and_smoke_detector' || modelKey === 'fire') {
    return 'fire-smoke.svg';
  }
  if (modelKey === 'smoke') {
    return 'smoke.svg';
  }
  if (
    modelKey.includes('multiple_rtsp') ||
    ['car', 'vehicle', 'bus', 'truck', 'police', 'ambulance', 'firetruck', 'emergency_car_detector'].includes(
      modelKey
    )
  ) {
    return 'car.svg';
  }
  if (modelKey === 'people' || modelKey === 'person' || modelKey === 'utrau_staff_detector') {
    return 'people.svg';
  }
  if (
    modelKey === 'smoking' ||
    modelKey === 'smoking_detector' ||
    modelKey === 'smoking_detector_v2' ||
    modelKey === 'cigarette'
  ) {
    return 'kyrenie.svg';
  }
  if (modelKey === 'garbage_truck_detector' || modelKey === 'garbage_truck') {
    return 'mysor.svg';
  }
  if (modelKey === 'tractor_detector' || modelKey === 'tractor') {
    return 'tractor.svg';
  }

  return 'lock.svg';
}

const AddCameraModal: React.FC<AddCameraModalProps> = ({
  areas: initialAreas,
  onClose,
  onSuccess,
  defaultAreaId = null,
}) => {
  const { areas: navigationAreas, openCreateArea } = useAreasNav();
  const areas = useMemo(() => Array.from(new Map([...initialAreas, ...navigationAreas].map((area) => [area.id, area])).values()), [initialAreas, navigationAreas]);
  const [areaId, setAreaId] = useState<number | ''>('');
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [rtsp, setRtsp] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [presenceCheckEnabled, setPresenceCheckEnabled] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelTitles, setModelTitles] = useState<Record<string, string>>({});
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  /** Оплата камеры: автопродление с баланса и нехватка средств. */
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [autoRenew, setAutoRenew] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [fundsError, setFundsError] = useState(false);
  const cameraMonthlyPrice = user?.camera_monthly_price ?? 1000;
  const userBalance = user?.balance ?? 0;
  const trialAvailable = !!user?.camera_trial_available;
  const initialPrice = trialAvailable ? 0 : cameraMonthlyPrice;
  const canAffordCamera = trialAvailable || userBalance >= cameraMonthlyPrice;

  const selectedHasPresenceModel = cameraHasPresenceCheckModel(selectedModels);

  useEffect(() => {
    if (areas.length === 0) {
      setAreaId('');
      return;
    }
    const preferred =
      defaultAreaId != null && areas.some((a) => a.id === defaultAreaId)
        ? defaultAreaId
        : areas[0].id;
    setAreaId((current) => areas.some((area) => area.id === current) ? current : preferred);
  }, [areas, defaultAreaId]);

  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await modelsAPI.getAll();
        if (!cancelled && response.success) {
          setModelsError('');
          setAvailableModels((response.models || []).map((model) => model.name));
          setModelTitles(Object.fromEntries((response.models || []).map((model) => [model.name, model.title])));
        } else if (!cancelled) {
          setAvailableModels([]);
          setModelsError(response.message || 'Не удалось загрузить модели');
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableModels([]);
          setModelsError((error as Error)?.message || 'Не удалось загрузить модели из внешнего API');
        }
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleModel = (modelName: string) => {
    setSelectedModels((prev) => {
      const next = prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName];
      if (!next.some(isPresenceCheckModel)) {
        setPresenceCheckEnabled(false);
      }
      return next;
    });
  };

  const advanceStep = () => {
    setError('');
    setFundsError(false);
    if (step === 0) {
      if (!areaId || !name.trim() || !rtsp.trim()) {
        setError('Заполните название, адрес камеры и выберите участок.');
        return;
      }
      if (!/^rtsps?:\/\/\S+$/i.test(rtsp.trim())) {
        setError('Проверьте адрес камеры: он должен начинаться с rtsp:// или rtsps://.');
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (modelsLoading) return;
      if (modelsError) {
        setError('Не удалось загрузить список моделей. Закройте мастер и попробуйте открыть его снова.');
        return;
      }
      setStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 2) {
      advanceStep();
      return;
    }

    if (!paymentConfirmed) {
      setError('Подтвердите данные и списание оплаты за первый месяц.');
      return;
    }

    if (!areaId || !name.trim() || !rtsp.trim()) {
      setError('Заполните все обязательные поля');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await camerasAPI.create(
        name.trim(),
        rtsp.trim(),
        Number(areaId),
        selectedModels,
        trialAvailable ? false : autoRenew
      );

      // С баланса списана оплата за первый месяц
      void refreshUser();

      if (!response.success) {
        setError(response.message || 'Ошибка добавления камеры');
        return;
      }

      if (response.queued) {
        notifyNotificationsChanged();
        onSuccess?.();
        onClose();
        return;
      }

      const extId = response.camera?.camera_id;
      // Счётчик людей временно отключён
      // if (extId && cameraHasPeopleCounterModel(selectedModels) && peopleCounterEnabled) {
      //   const pc = await camerasAPI.enablePeopleCounter(extId);
      //   if (!pc.success) {
      //     setError(pc.message || 'Камера создана, но не удалось включить счётчик людей');
      //     onSuccess?.();
      //     return;
      //   }
      // }
      if (extId && cameraHasPresenceCheckModel(selectedModels) && presenceCheckEnabled) {
        const pr = await camerasAPI.setPresenceSettings(extId, {
          ...DEFAULT_PRESENCE_SETTINGS,
          enabled: true,
        });
        if (!pr.success) {
          setError(pr.message || 'Камера создана, но не удалось включить проверку присутствия');
          onSuccess?.();
          return;
        }
        if (pr.queued) notifyNotificationsChanged();
      }

      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      if (isInsufficientFundsError(err)) {
        setFundsError(true);
        setError((err as any)?.response?.message || 'Недостаточно средств на балансе для оплаты камеры');
        void refreshUser();
        return;
      }
      setFundsError(false);
      const e = err as { message?: string; response?: { message?: string; rtsp_hint?: string } };
      let errorMessage = e.message || e.response?.message || 'Ошибка добавления камеры';
      if (e.response?.rtsp_hint) {
        errorMessage = `${errorMessage}\n\n${e.response.rtsp_hint}`;
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="area-modal camera-wizard" role="dialog" aria-modal="true" aria-labelledby="add-camera-title">
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog camera-wizard__dialog">
        <header className="area-modal__header">
          <div><h2 id="add-camera-title" className="area-modal__title">Добавить камеру</h2><p className="camera-wizard__subtitle">Настроим всё по шагам — это займёт пару минут</p></div>
          <button type="button" className="area-modal__close" aria-label="Закрыть" onClick={onClose}>
            ×
          </button>
        </header>

        {areas.length === 0 ? (
          <div className="area-modal__body">
            <p style={{ margin: 0, fontSize: '15px', color: '#6c6c70', lineHeight: 1.5 }}>
              Создайте участок, чтобы добавить в него камеру.
            </p>
            <footer className="area-modal__footer">
              <button type="button" className="area-modal__submit" onClick={async () => {
                const created = await openCreateArea();
                if (created) setAreaId(created.id);
              }}>Создать участок</button>
              <button
                type="button"
                className="area-modal__submit area-modal__submit--secondary"
                onClick={onClose}
              >
                Закрыть
              </button>
            </footer>
          </div>
        ) : (
          <>
          <div className="camera-wizard__steps" aria-label="Шаги настройки камеры">
            {['Подключение', 'Модели', 'Оплата'].map((title, index) => <div key={title} className={`camera-wizard__step${step === index ? ' is-current' : ''}${step > index ? ' is-done' : ''}`}>
              <span>{step > index ? '✓' : index + 1}</span><small>{title}</small>
            </div>)}
          </div>

          <form className="area-modal__body camera-wizard__body" onSubmit={handleSubmit}>
            {step === 0 && <section className="camera-wizard__panel">
              <h3>Подключение камеры</h3>
              <p className="camera-wizard__hint">Укажите название для списка, адрес камеры и участок, где она находится.</p>
              <div className="camera-wizard__fields">
                <label className="camera-wizard__field">Название камеры<input className="area-modal__input" type="text" placeholder="Например, Вход 1" value={name} onChange={(e) => setName(e.target.value)} required /></label>
                <label className="camera-wizard__field">Адрес камеры (RTSP)<input className="area-modal__input" type="url" placeholder="rtsp://…" value={rtsp} onChange={(e) => setRtsp(e.target.value)} required /><small>Адрес обычно указан в настройках камеры или предоставлен установщиком.</small></label>
                <label className="camera-wizard__field">Участок<select className="area-modal__input area-modal__input--select" value={areaId} onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')} required aria-label="Участок">
                  {areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
                </select></label>
              </div>
            </section>}

            {step === 1 && <section className="camera-wizard__panel">
              <h3>Выберите, что распознавать</h3>
              <p className="camera-wizard__hint">Можно выбрать несколько пунктов или пропустить выбор и добавить аналитику позже.</p>
              <div className="camera-wizard__model-list">
                {modelsLoading ? <div className="camera-wizard__empty">Загружаем список моделей…</div> : modelsError ? <div className="camera-wizard__empty is-error" role="alert">{modelsError}</div> : availableModels.length === 0 ? <div className="camera-wizard__empty">Сейчас нет доступных моделей.</div> : availableModels.map((model) => <label key={model} className={`area-modal-analytic${selectedModels.includes(model) ? ' is-selected' : ''}`}>
                  <span className="area-modal-analytic__icon"><img src={`${modalIconsBase}/${getModelModalIcon(model)}`} alt="" /></span>
                  <span className="area-modal-analytic__text">{modelTitles[model] || getModelDisplayName(model)}</span>
                  <input type="checkbox" checked={selectedModels.includes(model)} onChange={() => handleToggleModel(model)} />
                  <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                </label>)}
              </div>
              {selectedHasPresenceModel && <label className="camera-wizard__presence"><input type="checkbox" checked={presenceCheckEnabled} onChange={(e) => setPresenceCheckEnabled(e.target.checked)} /><span><strong>Включить контроль присутствия</strong><small>После добавления камеры можно будет отметить рабочие места и назвать их.</small></span><span className="area-modal-analytic__checkbox" aria-hidden="true" /></label>}
            </section>}

            {step === 2 && <section className="camera-wizard__panel">
              <h3>Проверьте и подтвердите</h3>
              <p className="camera-wizard__hint">{trialAvailable ? 'Первая камера бесплатно на 3 дня. Затем её можно продлить платно. Вторая и следующие камеры оплачиваются сразу.' : 'Камера будет добавлена в выбранный участок. Оплата за первый месяц спишется сразу.'}</p>
              <div className="camera-wizard__summary">
                <div><span>Название</span><strong>{name || '—'}</strong></div>
                <div><span>Участок</span><strong>{areas.find((area) => area.id === Number(areaId))?.name || '—'}</strong></div>
                <div><span>Модели</span><strong>{selectedModels.length ? selectedModels.map((model) => modelTitles[model] || getModelDisplayName(model)).join(', ') : 'Не выбраны'}</strong></div>
                {selectedHasPresenceModel && <div><span>Контроль присутствия</span><strong>{presenceCheckEnabled ? 'Включён' : 'Не включён'}</strong></div>}
              </div>
              <div className="area-modal__billing camera-wizard__billing">
                {trialAvailable && <div className="area-modal__billing-row"><span>Первые 3 дня</span><strong>Бесплатно</strong></div>}
                <div className="area-modal__billing-row"><span>Стоимость в месяц</span><strong>{cameraMonthlyPrice.toLocaleString('ru-RU')} ₽</strong></div>
                <div className="area-modal__billing-row"><span>Баланс после добавления</span><strong>{Math.max(0, userBalance - initialPrice).toLocaleString('ru-RU')} ₽</strong></div>
                <p className="area-modal__billing-note">{trialAvailable ? 'Сейчас деньги не списываются. Через 3 дня работа камеры приостановится до оплаты продления.' : 'Автопродление будет списывать оплату с баланса каждый месяц.'}</p>
                {!trialAvailable && <label className="area-modal__people-counter-option"><input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} /><span className="area-modal-analytic__checkbox" aria-hidden="true" /><span>Включить автопродление</span></label>}
                {!canAffordCamera && <div className="area-modal__billing-warning">Недостаточно средств: нужно {cameraMonthlyPrice.toLocaleString('ru-RU')} ₽. <button type="button" className="area-modal__billing-link" onClick={() => { onClose(); navigate('/balance'); }}>Пополнить баланс</button></div>}
              </div>
              <label className="camera-wizard__payment-confirm">
                <input type="checkbox" checked={paymentConfirmed} onChange={(event) => setPaymentConfirmed(event.target.checked)} />
                <span>{trialAvailable ? 'Я проверил данные. Добавить первую камеру бесплатно на 3 дня' : `Я проверил данные и подтверждаю списание ${cameraMonthlyPrice.toLocaleString('ru-RU')} ₽ за первый месяц`}</span>
              </label>
            </section>}

            {error && (
              <div
                style={{
                  color: '#ff4444',
                  padding: '10px',
                  marginTop: '10px',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {error}
                {fundsError && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="area-modal__billing-link"
                      onClick={() => {
                        onClose();
                        navigate('/balance');
                      }}
                    >
                      Пополнить баланс
                    </button>
                  </>
                )}
              </div>
            )}

            <footer className="area-modal__footer camera-wizard__footer">
              {step > 0 && <button type="button" className="area-modal__submit area-modal__submit--secondary" disabled={submitting} onClick={() => { setError(''); setFundsError(false); setPaymentConfirmed(false); setStep((current) => current - 1); }}>Назад</button>}
              {step < 2 ? <button type="button" className="area-modal__submit" disabled={submitting || (step === 1 && (modelsLoading || !!modelsError))} onClick={advanceStep}>Далее <span aria-hidden="true">→</span></button> : <button type="submit" className="area-modal__submit" disabled={submitting || !canAffordCamera || !paymentConfirmed}><span className="area-modal__submit-plus">+</span><span>{submitting ? 'Добавление…' : 'Подтвердить и добавить камеру'}</span></button>}
            </footer>
          </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AddCameraModal;
